import React, {useContext, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../utils/webInfo";
import callToast, {handleChangeData, postData, validateFormFields} from "../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../Components/PageHeading";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";

// Mapbox Imports
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

interface InitialState {
    name: string,
    address: string,
    contact_person: string,
    contact_number: string,
    latitude: number | string | null | undefined,
    longitude: number | string | null | undefined,

}

const FuelStation = () => {
    const initialState: InitialState = {
        name: '',
        address: '',
        contact_person: '',
        contact_number: '',
        latitude: null,
        longitude: null,
    };
    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<InitialState>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);
    const [mapModal, setMapModal] = useState<boolean>(false);


    const {page, limitData, handlePageChange} = usePagination();
    const mainTitle = "Fuel Stations"
    const tableDataEndpointUrl = `/vehicle-refuel-stations?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-refuel-stations/${updatedId}` : "/vehicle-refuel-stations";
    const {data: tableDataList, totalData, loading} = useFetch(tableDataEndpointUrl, [refresh])

    // map refs
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const showMapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);

    const initMap = (mode: 'edit' | 'view') => {
        // Choose the correct container based on the mode
        const targetContainer = mode === 'edit' ? mapContainerRef.current : showMapContainerRef.current;
        if (!targetContainer) return;

        mapboxgl.accessToken = webInfo.mapBoxToken;

        const rawLat = parseFloat(String(formData.latitude));
        const rawLng = parseFloat(String(formData.longitude));
        const isValidLat = !isNaN(rawLat) && rawLat >= -90 && rawLat <= 90;
        const isValidLng = !isNaN(rawLng) && rawLng >= -180 && rawLng <= 180;

        const finalLng = isValidLng ? rawLng : 90.4125;
        const finalLat = isValidLat ? rawLat : 23.8103;

        mapRef.current = new mapboxgl.Map({
            container: targetContainer,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [finalLng, finalLat],
            zoom: isValidLat && isValidLng ? 15 : 11
        });

        // Initialize Marker
        markerRef.current = new mapboxgl.Marker({
            draggable: mode === 'edit', // Only draggable in edit mode
            color: "#ef4444"
        })
            .setLngLat([finalLng, finalLat])
            .addTo(mapRef.current);

        if (mode === 'edit') {
            // Edit Mode Events: Click and Drag
            mapRef.current.on('click', (e) => {
                const {lng, lat} = e.lngLat;
                setFormData(prev => ({
                    ...prev,
                    longitude: parseFloat(lng.toFixed(6)),
                    latitude: parseFloat(lat.toFixed(6))
                }));
                markerRef.current?.setLngLat([lng, lat]);
            });

            // for drag
            // markerRef.current.on('dragend', () => {
            //     const lngLat = markerRef.current?.getLngLat();
            //     if (lngLat) {
            //         setFormData(prev => ({ ...prev, longitude: parseFloat(lngLat.lng.toFixed(6)), latitude: parseFloat(lngLat.lat.toFixed(6)) }));
            //     }
            // });

            // Add Geocoder only in Edit Mode
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                marker: false,
                countries: 'bd',
                placeholder: 'Search location...'
            });
            mapRef.current.addControl(geocoder, 'top-left');

            geocoder.on('result', (e: any) => {
                const [lng, lat] = e.result.geometry.coordinates;
                setFormData(prev => ({
                    ...prev,
                    longitude: parseFloat(lng.toFixed(6)),
                    latitude: parseFloat(lat.toFixed(6))
                }));
                markerRef.current?.setLngLat([lng, lat]);
            });
        }
    };

    const destroyMap = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
            markerRef.current = null;
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const validationRules = [
            {field: 'name', label: 'Name'},
            {field: 'address', label: 'Address'},
            {field: 'contact_person', label: 'Contact Person'},
            {field: 'contact_number', label: 'Contact Number'},
            {field: 'latitude', label: 'Location'},

        ];
        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            name: formData.name.replace(/\s+/g, ' ').trim(),
            address: formData?.address?.replace(/\s+/g, ' ').trim(),
            contact_person: formData?.contact_person?.replace(/\s+/g, ' ').trim(),
            contact_number: formData?.contact_number,
            latitude: formData?.latitude?.toString(),
            longitude: formData?.longitude?.toString(),

        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));
            if (response.ok) {
                callToast(toast, true, `${mainTitle} ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState);
                setModal(false);
                setUpdatedId(null);
                setRefresh(!refresh);
                destroyMap();
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }
        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }
    }

    const actionTemplate = (rowData: any) => {
        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button icon={'pi pi-pencil'} className={'border-rounded w-2rem h-2rem'} onClick={() => {
                    setUpdatedId(rowData?.id);
                    setFormData({
                        name: rowData?.name,
                        address: rowData?.address,
                        latitude: rowData?.latitude,
                        longitude: rowData?.longitude,
                        contact_person: rowData?.contact_person,
                        contact_number: rowData?.contact_number,
                    });
                    setModal(true);
                }} outlined/>
            </div>
        )
    }


    const addressBodyTemplate = (rowData: any) => {
        const hasLatLng = rowData.latitude && rowData.longitude;
        return (
            <span className={'w-full flex align-items-center gap-2 cursor-pointer'} onClick={() => {
                setFormData({
                    name: rowData?.name,
                    address: rowData?.address,
                    latitude: rowData?.latitude,
                    longitude: rowData?.longitude,
                    contact_person: rowData?.contact_person,
                    contact_number: rowData?.contact_number,
                });
                setMapModal(true);
            }}>
                {hasLatLng && <i className="pi pi-map-marker text-red-500" style={{fontSize: '1rem'}}/>}
                {rowData?.address || "No Address"}
            </span>
        );
    }


    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading title={mainTitle} isCreate={true} createFunction={() => setModal(true)} back={true}/>

            <DataTable
                value={tableDataList}
                dataKey="id"
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column field="name" header="Name" style={{width: '25%'}} headerClassName={"white-space-nowrap"}/>
                <Column field="address" header="Address" style={{width: '25%'}} body={addressBodyTemplate}
                        headerClassName={"white-space-nowrap"}/>
                <Column field="contact_person" header="Contact Person" style={{width: '25%'}}
                        headerClassName={"white-space-nowrap"}/>
                <Column field="contact_number" header="Contact Number" style={{width: '25%'}}
                        headerClassName={"white-space-nowrap"}/>

                <Column header={"Action"} body={actionTemplate} headerClassName={"flex justify-content-end"}/>
            </DataTable>

            {/* EDIT/CREATE MODAL */}
            <Dialog visible={modal} style={{width: "50rem"}}
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`} onHide={() => {
                setModal(false);
                setUpdatedId(null);
                setFormData(initialState);
                destroyMap();
            }} onShow={() => initMap('edit')}>
                <form onSubmit={handleSubmit}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <Label label={"Name"} required={!updatedId}/>
                            <InputText
                                name="name"
                                id={"name"}
                                placeholder={"Fuel Station Name"}
                                value={formData.name}
                                onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                                className="w-full"

                            />
                        </div>
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12 md:col-5">
                            <Label label={"Contact Person"} required={!updatedId}/>
                            <InputText
                                value={formData.contact_person}
                                placeholder={"Contact person name"}
                                id={"contact_person"}
                                name={"contact_person"}
                                className="w-full"
                                onChange={(e) => handleChangeData('contact_person', e.target.value, formData, setFormData)}

                            />
                        </div>

                        <div className="col-12 md:col-5">
                            <Label label={"Contact Person"} required={!updatedId}/>
                            <InputText
                                value={formData.contact_number}
                                placeholder={"Contact number"}
                                id={"contact_number"}
                                name={"contact_number"}
                                className="w-full"
                                keyfilter={"pnum"}
                                size={11}
                                onChange={(e) => handleChangeData('contact_number', e.target.value, formData, setFormData)}

                            />
                        </div>
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12">
                            <Label label={"Address"} required={!updatedId}/>
                            <InputTextarea
                                value={formData.address}
                                placeholder={"Parking campus address"}
                                id={"address"}
                                name={"address"}
                                className="w-full"
                                onChange={(e) => handleChangeData('address', e.target.value, formData, setFormData)}
                                rows={3}

                            />
                        </div>
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12">
                            {/*<div className="flex justify-content-between mb-2">*/}
                            {/*    <Label label={"Location"}/>*/}
                            {/*<div className="text-sm font-medium surface-200 px-2 py-1 border-round border-1 border-300">*/}
                            {/*    Lat: {formData.latitude || '---'} | Lng: {formData.longitude || '---'}*/}
                            {/*</div>*/}
                            {/*</div>*/}
                            <Label label={"Location"} required={!updatedId}/>
                            <div ref={mapContainerRef} style={{
                                height: '300px',
                                width: '100%',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6'
                            }}/>
                        </div>
                    </div>
                    <Button
                        type="submit"
                        label={updatedId ? 'Update' : "Create"}
                        className="p-button-primary mt-4"
                        size={"small"}
                        loading={isSubmitting}
                    />
                </form>
            </Dialog>

            {/* VIEW MAP MODAL */}
            <Dialog
                visible={mapModal}
                style={{width: "50rem"}}
                header={"Location Details"}
                onHide={() => {
                    setMapModal(false);
                    setFormData(initialState);
                    destroyMap();
                }
                }
                onShow={() => initMap('view')}>
                <div className="grid">
                    <div className="col-12">
                        <div className="surface-100 p-3 border-round mb-3">
                            <div className="font-bold text-lg mb-1">{formData.name}</div>
                            <div className="text-600 mb-2"><i className="pi pi-map-marker mr-1"/>{formData.address}
                            </div>
                        </div>
                        <div ref={showMapContainerRef} style={{
                            height: '400px',
                            width: '100%',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                        }}/>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default FuelStation;